<template>
  <div :class="['ecs-page', { 'is-embedded': isEmbedded, 'content': !isEmbedded }]">
    <!-- 1. TOP HEADER CARD -->
    <div class="ecs-header-card row-data">
      <div class="ecs-header-main">
        <div v-if="!isEmbedded" class="ecs-header-nav">
          <el-button
            class="ecs-back-btn"
            type="default"
            size="small"
            icon="el-icon-back"
            @click="goBackToEvaluation"
          >
            Quay lại nhận xét
          </el-button>
          <div class="ecs-breadcrumb-text">
            <span>Học sinh</span>
            <i class="el-icon-arrow-right ecs-bc-sep" />
            <span>Nhận xét học sinh</span>
            <i class="el-icon-arrow-right ecs-bc-sep" />
            <span class="ecs-bc-current">Cấu hình danh mục & Mẫu</span>
          </div>
        </div>

        <div class="ecs-title-section">
          <div class="ecs-title-icon-wrap">
            <i class="el-icon-s-opportunity" />
          </div>
          <div class="ecs-title-content">
            <h1 class="ecs-page-title">Cấu hình Danh mục & Mẫu nhận xét</h1>
            <p class="ecs-page-subtitle">
              Quản lý các tiêu chí đánh giá và ngân hàng mẫu câu nhận xét định sẵn để giáo viên dễ dàng áp dụng khi nhận xét học sinh
            </p>
          </div>
        </div>
      </div>

      <div class="ecs-header-stats">
        <div class="ecs-stat-item">
          <div class="ecs-stat-icon"><i class="el-icon-folder" /></div>
          <div class="ecs-stat-meta">
            <span class="ecs-stat-val">{{ categories.length }}</span>
            <span class="ecs-stat-lbl">Danh mục</span>
          </div>
        </div>
        <div class="ecs-stat-divider" />
        <div class="ecs-stat-item">
          <div class="ecs-stat-icon ecs-stat-icon--highlight"><i class="el-icon-document" /></div>
          <div class="ecs-stat-meta">
            <span class="ecs-stat-val">{{ selectedCategory ? templates.length : '—' }}</span>
            <span class="ecs-stat-lbl">{{ selectedCategory ? 'Mẫu đang chọn' : 'Chưa chọn mục' }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. MAIN 2-COLUMN GRID -->
    <div class="ecs-grid-container row-data">
      <!-- 2.1. LEFT PANEL: CATEGORIES -->
      <div class="ecs-panel-card ecs-panel-categories">
        <div class="ecs-panel-header">
          <div class="ecs-panel-title-wrap">
            <div class="ecs-panel-icon"><i class="el-icon-folder-opened" /></div>
            <div>
              <h2 class="ecs-panel-title">
                Danh mục đánh giá
                <span class="ecs-badge">{{ categories.length }}</span>
              </h2>
              <p class="ecs-panel-desc">Nhấp chọn danh mục để xem mẫu nhận xét tương ứng</p>
            </div>
          </div>
          <el-button
            type="primary"
            size="small"
            icon="el-icon-plus"
            class="ecs-btn-primary"
            @click="openCreateCategory"
          >
            Thêm danh mục
          </el-button>
        </div>

        <div class="ecs-table-container">
          <el-table
            v-loading="categoryLoading"
            :data="categories"
            border
            class="sk-table ecs-cat-table"
            size="small"
            highlight-current-row
            :row-class-name="getCategoryRowClass"
            style="width: 100%"
            @row-click="handleCategoryRowClick"
          >
            <el-table-column label="STT" type="index" width="50" align="center" />
            <el-table-column label="Tên danh mục" prop="name" min-width="160">
              <template slot-scope="{ row }">
                <div class="ecs-cat-name-cell">
                  <i :class="['el-icon-folder', { 'is-active': selectedCategoryId === row.id }]" />
                  <span class="ecs-cat-name-text">{{ row.name }}</span>
                  <el-tag
                    v-if="selectedCategoryId === row.id"
                    size="mini"
                    type="primary"
                    effect="dark"
                    class="ecs-active-pill"
                  >
                    Đang chọn
                  </el-tag>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Thứ tự" prop="orderIndex" width="65" align="center" />
            <el-table-column label="Thao tác" width="95" align="center" fixed="right">
              <template slot-scope="{ row }">
                <div class="ecs-row-actions" @click.stop>
                  <el-tooltip content="Chỉnh sửa danh mục" placement="top">
                    <el-button
                      type="primary"
                      icon="el-icon-edit"
                      size="mini"
                      circle
                      plain
                      class="ecs-circle-btn"
                      @click="openEditCategory(row)"
                    />
                  </el-tooltip>
                  <el-tooltip content="Xoá danh mục" placement="top">
                    <el-button
                      type="danger"
                      icon="el-icon-delete"
                      size="mini"
                      circle
                      plain
                      class="ecs-circle-btn ml-1"
                      @click="handleDeleteCategory(row)"
                    />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 2.2. RIGHT PANEL: TEMPLATES -->
      <div class="ecs-panel-card ecs-panel-templates">
        <div class="ecs-panel-header">
          <div class="ecs-panel-title-wrap">
            <div class="ecs-panel-icon ecs-panel-icon--doc"><i class="el-icon-document-copy" /></div>
            <div>
              <h2 class="ecs-panel-title">
                Mẫu nhận xét
                <span v-if="selectedCategory" class="ecs-badge ecs-badge--active">
                  {{ templates.length }} mẫu
                </span>
              </h2>
              <p class="ecs-panel-desc">
                <span v-if="selectedCategory">
                  Danh mục: <strong>{{ selectedCategory.name }}</strong>
                </span>
                <span v-else>Chọn một danh mục để tải danh sách mẫu</span>
              </p>
            </div>
          </div>

          <div class="ecs-panel-actions">
            <el-select
              v-model="selectedCategoryId"
              placeholder="Chọn danh mục..."
              size="small"
              class="ecs-cat-selector"
              @change="fetchTemplates"
            >
              <el-option
                v-for="cat in categories"
                :key="cat.id"
                :label="cat.name"
                :value="cat.id"
              />
            </el-select>
            <el-button
              type="primary"
              size="small"
              icon="el-icon-plus"
              class="ecs-btn-primary"
              :disabled="!selectedCategoryId"
              @click="openCreateTemplate"
            >
              Thêm mẫu
            </el-button>
          </div>
        </div>

        <div class="ecs-table-container">
          <!-- Empty State: No category selected -->
          <div v-if="!selectedCategoryId && !templateLoading" class="ecs-empty-state">
            <div class="ecs-empty-icon"><i class="el-icon-folder-opened" /></div>
            <h3 class="ecs-empty-title">Chưa chọn danh mục đánh giá</h3>
            <p class="ecs-empty-text">
              Nhấp vào một dòng danh mục ở bảng bên trái hoặc chọn từ danh sách thả xuống ở góc trên để hiển thị các mẫu nhận xét.
            </p>
          </div>

          <!-- Empty State: Category selected but 0 templates -->
          <div v-else-if="selectedCategoryId && templates.length === 0 && !templateLoading" class="ecs-empty-state">
            <div class="ecs-empty-icon ecs-empty-icon--add"><i class="el-icon-document-add" /></div>
            <h3 class="ecs-empty-title">Chưa có mẫu nhận xét nào</h3>
            <p class="ecs-empty-text">
              Danh mục "<strong>{{ selectedCategoryName }}</strong>" hiện chưa có câu mẫu nhận xét nào.
            </p>
            <el-button
              type="primary"
              size="small"
              icon="el-icon-plus"
              class="ecs-btn-primary mt-3"
              @click="openCreateTemplate"
            >
              Thêm mẫu nhận xét đầu tiên
            </el-button>
          </div>

          <!-- Table of templates -->
          <el-table
            v-else
            v-loading="templateLoading"
            :data="templates"
            border
            class="sk-table ecs-tpl-table"
            size="small"
            style="width: 100%"
          >
            <el-table-column label="STT" type="index" width="50" align="center" />
            <el-table-column label="Nội dung mẫu nhận xét" prop="content" min-width="300">
              <template slot-scope="{ row }">
                <div class="ecs-tpl-content-cell">
                  <div :class="['ecs-desc', { expanded: isDescExpanded(row.id) }]">
                    {{ row.content }}
                  </div>
                  <div class="ecs-tpl-footer-row">
                    <a
                      v-if="showDescToggle(row.content)"
                      href="#"
                      class="ecs-expand-link"
                      @click.prevent="toggleDesc(row.id)"
                    >
                      {{ isDescExpanded(row.id) ? 'Thu gọn' : 'Xem thêm' }}
                    </a>
                    <el-tooltip content="Sao chép nội dung" placement="top">
                      <button
                        type="button"
                        class="ecs-copy-btn"
                        @click="copyTemplateText(row.content)"
                      >
                        <i class="el-icon-document-copy" /> Sao chép
                      </button>
                    </el-tooltip>
                  </div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="Thao tác" width="95" align="center" fixed="right">
              <template slot-scope="{ row }">
                <div class="ecs-row-actions">
                  <el-tooltip content="Chỉnh sửa mẫu" placement="top">
                    <el-button
                      type="primary"
                      icon="el-icon-edit"
                      size="mini"
                      circle
                      plain
                      class="ecs-circle-btn"
                      @click="openEditTemplate(row)"
                    />
                  </el-tooltip>
                  <el-tooltip content="Xoá mẫu nhận xét" placement="top">
                    <el-button
                      type="danger"
                      icon="el-icon-delete"
                      size="mini"
                      circle
                      plain
                      class="ecs-circle-btn ml-1"
                      @click="handleDeleteTemplate(row)"
                    />
                  </el-tooltip>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>
    </div>

    <!-- 3. CATEGORY DIALOG -->
    <DialogBase
      :visible.sync="categoryDialogVisible"
      :title="categoryDialogTitle"
      size="sm"
      :confirm-loading="categorySaving"
      confirm-text="Lưu danh mục"
      cancel-text="Huỷ bỏ"
      :use-panel="true"
      panel-title="Danh mục đánh giá"
      :panel-subtitle="categoryEditMode ? 'Cập nhật thông tin danh mục đánh giá' : 'Tạo mới danh mục tiêu chí đánh giá'"
      panel-icon="el-icon-folder"
      @confirm="handleSaveCategory"
      @cancel="categoryDialogVisible = false"
    >
      <el-form
        ref="categoryForm"
        :model="categoryForm"
        :rules="categoryRules"
        label-position="top"
        class="ecs-dialog-form"
      >
        <el-form-item label="Tên danh mục" prop="name">
          <el-input
            v-model="categoryForm.name"
            placeholder="VD: Học tập, Kỹ năng sống, Nề nếp, Thể chất..."
            maxlength="100"
            clearable
            show-word-limit
          />
        </el-form-item>

        <!-- Quick suggestion tags for category -->
        <div v-if="!categoryEditMode" class="ecs-quick-tags-section">
          <span class="ecs-quick-tags-label"><i class="el-icon-magic-stick" /> Gợi ý nhanh:</span>
          <div class="ecs-quick-tags-list">
            <span
              v-for="tag in presetCategoryTags"
              :key="tag"
              class="ecs-quick-tag"
              @click="selectPresetTag(tag)"
            >
              + {{ tag }}
            </span>
          </div>
        </div>

        <el-form-item label="Thứ tự hiển thị" prop="orderIndex" class="mt-3">
          <el-input-number
            v-model="categoryForm.orderIndex"
            :min="1"
            :max="99"
            placeholder="Thứ tự"
            controls-position="right"
            style="width: 100%"
          />
          <span class="ecs-form-hint">Số nhỏ hơn sẽ được ưu tiên hiển thị trước trong bảng đánh giá</span>
        </el-form-item>
      </el-form>
    </DialogBase>

    <!-- 4. TEMPLATE DIALOG -->
    <DialogBase
      :visible.sync="templateDialogVisible"
      :title="templateDialogTitle"
      size="md"
      :confirm-loading="templateSaving"
      confirm-text="Lưu mẫu"
      cancel-text="Huỷ bỏ"
      :use-panel="true"
      panel-title="Mẫu nhận xét"
      :panel-subtitle="templateEditMode ? 'Cập nhật nội dung câu mẫu nhận xét' : 'Tạo mới nội dung câu mẫu nhận xét'"
      panel-icon="el-icon-document-add"
      @confirm="handleSaveTemplate"
      @cancel="templateDialogVisible = false"
    >
      <div v-if="selectedCategory" class="ecs-dialog-context-banner">
        <i class="el-icon-folder" />
        <span>Thuộc danh mục: <strong>{{ selectedCategory.name }}</strong></span>
      </div>

      <el-form
        ref="templateForm"
        :model="templateForm"
        :rules="templateRules"
        label-position="top"
        class="ecs-dialog-form"
      >
        <el-form-item label="Nội dung mẫu nhận xét" prop="content">
          <el-input
            v-model="templateForm.content"
            type="textarea"
            :rows="5"
            placeholder="Nhập nội dung mẫu nhận xét chi tiết (VD: Bé ngoan ngoãn, tích cực phát biểu xây dựng bài và phối hợp tốt cùng cô giáo trong các hoạt động trên lớp)..."
            maxlength="500"
            show-word-limit
          />
          <div class="ecs-form-tip">
            <i class="el-icon-info" />
            <span>Mẹo: Nội dung nên ngắn gọn, tích cực và súc tích để giáo viên dễ dàng áp dụng khi đánh giá học sinh.</span>
          </div>
        </el-form-item>
      </el-form>
    </DialogBase>
  </div>
</template>

<script>
import EvaluationCategoryService from "@/modules/evaluation/services/EvaluationCategoryService";
import DialogBase from "@/components/DialogBase.vue";
import NotifyMessage from "@/commonFuncion/NotifyMessage";

export default {
  name: "EvaluationCategorySettingsPage",
  components: { DialogBase },
  props: {
    isEmbedded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      // ─── Category ─────────────────────────────────
      categories: [],
      categoryLoading: false,
      categoryDialogVisible: false,
      categoryEditMode: false,
      categoryEditId: null,
      categorySaving: false,
      categoryForm: {
        name: "",
        orderIndex: null,
      },
      categoryRules: {
        name: [
          { required: true, message: "Tên danh mục không được để trống", trigger: "blur" },
          { max: 100, message: "Tên danh mục không được vượt quá 100 ký tự", trigger: "blur" },
        ],
      },
      presetCategoryTags: [
        "Học tập",
        "Kỹ năng sống",
        "Nề nếp & Kỷ luật",
        "Thể chất & Vận động",
        "Ăn uống & Dinh dưỡng",
        "Giấc ngủ",
        "Giao tiếp & Ứng xử",
        "Thái độ & Cảm xúc",
      ],

      // ─── Template ─────────────────────────────────
      selectedCategoryId: null,
      templates: [],
      templateLoading: false,
      templateDialogVisible: false,
      templateEditMode: false,
      templateEditId: null,
      templateSaving: false,
      templateForm: {
        content: "",
      },
      templateRules: {
        content: [
          { required: true, message: "Nội dung mẫu nhận xét không được để trống", trigger: "blur" },
          { max: 500, message: "Nội dung mẫu không được vượt quá 500 ký tự", trigger: "blur" },
        ],
      },

      // ─── Expand/Collapse ──────────────────────────
      expandedDescriptionIds: [],
    };
  },
  computed: {
    selectedCategory() {
      if (!this.selectedCategoryId) return null;
      return this.categories.find((c) => c.id === this.selectedCategoryId) || null;
    },
    selectedCategoryName() {
      return this.selectedCategory ? this.selectedCategory.name : "";
    },
    categoryDialogTitle() {
      return this.categoryEditMode ? "Chỉnh sửa danh mục" : "Thêm danh mục mới";
    },
    templateDialogTitle() {
      return this.templateEditMode ? "Chỉnh sửa mẫu nhận xét" : "Thêm mẫu nhận xét mới";
    },
  },
  async created() {
    await this.fetchCategories();
    if (this.categories.length > 0) {
      this.selectedCategoryId = this.categories[0].id;
      await this.fetchTemplates();
    }
  },
  methods: {
    goBackToEvaluation() {
      this.$router.push({ path: "/evaluation-v4" });
    },

    selectPresetTag(tag) {
      this.categoryForm.name = tag;
    },

    getCategoryRowClass({ row }) {
      return row.id === this.selectedCategoryId ? "is-selected-category-row" : "";
    },

    handleCategoryRowClick(row) {
      if (this.selectedCategoryId === row.id) return;
      this.selectedCategoryId = row.id;
      this.fetchTemplates();
    },

    async copyTemplateText(text) {
      if (!text) return;
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          const textArea = document.createElement("textarea");
          textArea.value = text;
          document.body.appendChild(textArea);
          textArea.select();
          document.execCommand("copy");
          document.body.removeChild(textArea);
        }
        NotifyMessage.leftCornerNotificationSuccess("Đã sao chép nội dung mẫu!");
      } catch {
        NotifyMessage.leftCornerNotificationError("Không thể sao chép nội dung.");
      }
    },

    // ─── Category Methods ────────────────────────────
    async fetchCategories() {
      this.categoryLoading = true;
      try {
        const res = await EvaluationCategoryService.getCategories();
        this.categories = res.data.data || [];
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải danh mục!"
        );
        this.categories = [];
      } finally {
        this.categoryLoading = false;
      }
    },

    openCreateCategory() {
      this.categoryEditMode = false;
      this.categoryEditId = null;
      this.categoryForm = { name: "", orderIndex: this.categories.length + 1 };
      this.categoryDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.categoryForm?.clearValidate();
      });
    },

    openEditCategory(row) {
      this.categoryEditMode = true;
      this.categoryEditId = row.id;
      this.categoryForm = { name: row.name, orderIndex: row.orderIndex };
      this.categoryDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.categoryForm?.clearValidate();
      });
    },

    async handleSaveCategory() {
      const valid = await this.$refs.categoryForm.validate().catch(() => false);
      if (!valid) return;

      this.categorySaving = true;
      try {
        if (this.categoryEditMode) {
          await EvaluationCategoryService.updateCategory(this.categoryEditId, this.categoryForm);
          NotifyMessage.leftCornerNotificationSuccess("Cập nhật danh mục thành công!");
        } else {
          await EvaluationCategoryService.createCategory(this.categoryForm);
          NotifyMessage.leftCornerNotificationSuccess("Tạo danh mục thành công!");
        }
        this.categoryDialogVisible = false;
        await this.fetchCategories();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Có lỗi xảy ra!"
        );
      } finally {
        this.categorySaving = false;
      }
    },

    async handleDeleteCategory(row) {
      try {
        await this.$confirm(
          `Xoá danh mục "${row.name}"? Tất cả mẫu nhận xét thuộc danh mục này cũng sẽ bị xoá.`,
          "Xác nhận xoá danh mục",
          {
            confirmButtonText: "Xoá danh mục",
            cancelButtonText: "Huỷ bỏ",
            type: "warning",
            confirmButtonClass: "el-button--danger",
          }
        );
      } catch {
        return;
      }

      try {
        await EvaluationCategoryService.deleteCategory(row.id);
        NotifyMessage.leftCornerNotificationSuccess("Xoá danh mục thành công!");
        await this.fetchCategories();
        if (this.selectedCategoryId === row.id) {
          this.selectedCategoryId = this.categories.length > 0 ? this.categories[0].id : null;
          if (this.selectedCategoryId) {
            await this.fetchTemplates();
          } else {
            this.templates = [];
          }
        }
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Xoá thất bại!"
        );
      }
    },

    // ─── Template Methods ────────────────────────────
    async fetchTemplates() {
      if (!this.selectedCategoryId) {
        this.templates = [];
        return;
      }
      this.templateLoading = true;
      try {
        const res = await EvaluationCategoryService.getTemplates(this.selectedCategoryId);
        this.templates = res.data.data || [];
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Không thể tải mẫu nhận xét!"
        );
        this.templates = [];
      } finally {
        this.templateLoading = false;
      }
    },

    openCreateTemplate() {
      if (!this.selectedCategoryId) {
        NotifyMessage.leftCornerNotificationWarning("Vui lòng chọn danh mục trước!");
        return;
      }
      this.templateEditMode = false;
      this.templateEditId = null;
      this.templateForm = { content: "" };
      this.templateDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.templateForm?.clearValidate();
      });
    },

    openEditTemplate(row) {
      this.templateEditMode = true;
      this.templateEditId = row.id;
      this.templateForm = { content: row.content };
      this.templateDialogVisible = true;
      this.$nextTick(() => {
        this.$refs.templateForm?.clearValidate();
      });
    },

    async handleSaveTemplate() {
      const valid = await this.$refs.templateForm.validate().catch(() => false);
      if (!valid) return;

      this.templateSaving = true;
      try {
        if (this.templateEditMode) {
          await EvaluationCategoryService.updateTemplate(this.templateEditId, {
            content: this.templateForm.content,
          });
          NotifyMessage.leftCornerNotificationSuccess("Cập nhật mẫu nhận xét thành công!");
        } else {
          await EvaluationCategoryService.createTemplate({
            categoryId: this.selectedCategoryId,
            content: this.templateForm.content,
          });
          NotifyMessage.leftCornerNotificationSuccess("Tạo mẫu nhận xét thành công!");
        }
        this.templateDialogVisible = false;
        await this.fetchTemplates();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Có lỗi xảy ra!"
        );
      } finally {
        this.templateSaving = false;
      }
    },

    async handleDeleteTemplate(row) {
      try {
        await this.$confirm(
          "Xoá mẫu nhận xét này? Thao tác không thể hoàn tác.",
          "Xác nhận xoá mẫu",
          {
            confirmButtonText: "Xoá mẫu",
            cancelButtonText: "Huỷ bỏ",
            type: "warning",
            confirmButtonClass: "el-button--danger",
          }
        );
      } catch {
        return;
      }

      try {
        await EvaluationCategoryService.deleteTemplate(row.id);
        NotifyMessage.leftCornerNotificationSuccess("Xoá mẫu nhận xét thành công!");
        await this.fetchTemplates();
      } catch (err) {
        NotifyMessage.leftCornerNotificationError(
          err?.response?.data?.message || "Xoá thất bại!"
        );
      }
    },

    // ─── Expand/Collapse ─────────────────────────────
    isDescExpanded(id) {
      return this.expandedDescriptionIds.includes(id);
    },
    toggleDesc(id) {
      const idx = this.expandedDescriptionIds.indexOf(id);
      if (idx === -1) this.expandedDescriptionIds.push(id);
      else this.expandedDescriptionIds.splice(idx, 1);
    },
    showDescToggle(content) {
      return content && String(content).length > 80;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/style/sk-table";
@import "@/assets/style/sk-dialog";

/* ─── Base Page ─────────────────────────────────── */
.ecs-page {
  padding: 8px 0 24px;
  .row-data { margin-bottom: 20px; }
}

/* ─── 1. Header Card ────────────────────────────── */
.ecs-header-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  padding: 20px 24px;
  background: linear-gradient(135deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  box-shadow: 0 8px 24px -4px rgba(15, 23, 42, 0.06);
  flex-wrap: wrap;
}

.ecs-header-main {
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 320px;
  flex: 1;
}

.ecs-header-nav {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.ecs-back-btn {
  font-weight: 600;
  border-radius: 12px;
  padding: 8px 16px;
  background: #ffffff;
  color: #334155;
  border: 1px solid #cbd5e1;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;

  &:hover {
    background: #f1f5f9;
    color: #1e40af;
    border-color: #93c5fd;
    transform: translateX(-2px);
  }
}

.ecs-breadcrumb-text {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #64748b;

  .ecs-bc-sep {
    font-size: 11px;
    color: #94a3b8;
  }

  .ecs-bc-current {
    font-weight: 600;
    color: #1e293b;
  }
}

.ecs-title-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.ecs-title-icon-wrap {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 24px;
  box-shadow: 0 6px 16px rgba(37, 99, 235, 0.28);
  flex-shrink: 0;
}

.ecs-title-content {
  display: flex;
  flex-direction: column;
}

.ecs-page-title {
  margin: 0 0 4px;
  font-size: 19px;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.2px;
}

.ecs-page-subtitle {
  margin: 0;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.ecs-header-stats {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 12px 18px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.03);
}

.ecs-stat-item {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ecs-stat-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;

  &--highlight {
    background: #ecfdf5;
    color: #059669;
  }
}

.ecs-stat-meta {
  display: flex;
  flex-direction: column;
}

.ecs-stat-val {
  font-size: 18px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
}

.ecs-stat-lbl {
  font-size: 11.5px;
  font-weight: 500;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.ecs-stat-divider {
  width: 1px;
  height: 32px;
  background: #e2e8f0;
}

/* ─── 2. 2-Column Grid ──────────────────────────── */
.ecs-grid-container {
  display: grid;
  grid-template-columns: minmax(360px, 4.2fr) minmax(480px, 6.8fr);
  gap: 20px;
  align-items: start;
}

@media (max-width: 1180px) {
  .ecs-grid-container {
    grid-template-columns: 1fr;
  }
}

.ecs-panel-card {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  padding: 18px 20px 20px;
  box-shadow: 0 8px 24px -4px rgba(15, 23, 42, 0.05);
  display: flex;
  flex-direction: column;
}

.ecs-panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  flex-wrap: wrap;
}

.ecs-panel-title-wrap {
  display: flex;
  align-items: center;
  gap: 12px;
}

.ecs-panel-icon {
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: linear-gradient(135deg, #e0e7ff 0%, #c7d2fe 100%);
  color: #4338ca;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  flex-shrink: 0;

  &--doc {
    background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%);
    color: #1d4ed8;
  }
}

.ecs-panel-title {
  margin: 0 0 2px;
  font-size: 16px;
  font-weight: 700;
  color: #0f172a;
  display: flex;
  align-items: center;
  gap: 8px;
}

.ecs-panel-desc {
  margin: 0;
  font-size: 12.5px;
  color: #64748b;
}

.ecs-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 20px;
  background: #f1f5f9;
  color: #475569;

  &--active {
    background: #dbeafe;
    color: #1e40af;
  }
}

.ecs-panel-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.ecs-cat-selector {
  width: 170px;
}

.ecs-btn-primary {
  font-weight: 600;
  border-radius: 10px;
  box-shadow: 0 2px 6px rgba(37, 99, 235, 0.2);
}

.ecs-table-container {
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #f1f5f9;
}

/* ─── Category Table ────────────────────────────── */
.ecs-cat-table {
  /deep/ tbody tr {
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover > td {
      background-color: #f8fafc !important;
    }

    &.is-selected-category-row > td {
      background-color: #eff6ff !important;
    }
  }
}

.ecs-cat-name-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  color: #1e293b;

  i {
    color: #94a3b8;
    font-size: 15px;

    &.is-active {
      color: #2563eb;
    }
  }
}

.ecs-cat-name-text {
  flex: 1;
  font-size: 13.5px;
}

.ecs-active-pill {
  font-size: 10.5px;
  padding: 0 6px;
  height: 20px;
  line-height: 20px;
  border-radius: 10px;
}

/* ─── Template Table & Cells ────────────────────── */
.ecs-tpl-content-cell {
  padding: 4px 0;
}

.ecs-tpl-footer-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed #f1f5f9;
}

.ecs-expand-link {
  color: #2563eb;
  font-size: 12.5px;
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.ecs-copy-btn {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;

  &:hover {
    background: #eff6ff;
    color: #2563eb;
    border-color: #bfdbfe;
  }
}

.ecs-row-actions {
  display: flex;
  align-items: center;
  justify-content: center;
}

.ecs-circle-btn {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.ecs-desc {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;
  color: #334155;
  line-height: 1.55;
  font-size: 13.5px;
}

.ecs-desc.expanded {
  -webkit-line-clamp: unset;
  overflow: visible;
}

/* ─── Empty States ──────────────────────────────── */
.ecs-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 48px 24px;
  background: #f8fafc;
  border-radius: 14px;
}

.ecs-empty-icon {
  width: 64px;
  height: 64px;
  border-radius: 20px;
  background: #e2e8f0;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
  margin-bottom: 14px;

  &--add {
    background: #dbeafe;
    color: #2563eb;
  }
}

.ecs-empty-title {
  margin: 0 0 6px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
}

.ecs-empty-text {
  margin: 0;
  max-width: 380px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

/* ─── Dialog Custom Enhancements ────────────────── */
.ecs-dialog-form {
  padding-top: 4px;
}

.ecs-quick-tags-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: -6px;
  margin-bottom: 14px;
  padding: 10px 12px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
}

.ecs-quick-tags-label {
  font-size: 12px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 4px;
}

.ecs-quick-tags-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.ecs-quick-tag {
  display: inline-block;
  padding: 3px 10px;
  background: #ffffff;
  border: 1px solid #cbd5e1;
  border-radius: 14px;
  font-size: 12px;
  color: #334155;
  cursor: pointer;
  transition: all 0.15s ease;
  user-select: none;

  &:hover {
    background: #eff6ff;
    border-color: #93c5fd;
    color: #1d4ed8;
    transform: translateY(-1px);
  }
}

.ecs-form-hint {
  display: block;
  margin-top: 6px;
  font-size: 12px;
  color: #94a3b8;
  line-height: 1.4;
}

.ecs-dialog-context-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  padding: 10px 14px;
  border-radius: 12px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1e40af;
  font-size: 13.5px;

  i {
    font-size: 16px;
  }
}

.ecs-form-tip {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
  line-height: 1.45;

  i {
    color: #3b82f6;
    margin-top: 2px;
  }
}

/* ─── Table Overrides ───────────────────────────── */
/deep/ .sk-table thead th,
/deep/ .sk-table thead td {
  background: #2563eb !important;
  color: #ffffff !important;
  font-weight: 700;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
/deep/ .sk-table thead th > .cell,
/deep/ .sk-table thead td > .cell {
  color: #ffffff !important;
  font-weight: 700;
}
/deep/ .sk-table thead tr { height: 48px; }
/deep/ .sk-table tbody td { background: #ffffff; }

/* ─── Input / Button Overrides ──────────────────── */
/deep/ .ecs-page .el-input__inner,
/deep/ .ecs-page .el-select .el-input__inner { border-radius: 10px; }
/deep/ .ecs-page .el-button { border-radius: 10px; }

/* ─── Utilities ─────────────────────────────────── */
.mt-3 { margin-top: 12px; }
.ml-1 { margin-left: 4px; }
</style>
